import CancelledJob from '~/components/CancelledJob';
import {
  CancelShiftRefundDialog,
  type ICancelShiftRefundDialogProps,
} from '~/components/shared/CancelShiftRefundDialog';
import { useCancelJobMutation } from '~/domain/Jobs';
import { useStandardCancelFlow } from '~/hooks/useStandardCancelFlow';
import type { IJob } from '~/models';

type TJobDetailsCancelJob = ICancelShiftRefundDialogProps['job'] & Pick<IJob, 'id'>;

export interface IJobDetailsCancelPopupProps {
  // JobDetails' mapStateToProps gives null while the page's job id and the redux job differ.
  job: TJobDetailsCancelJob | null;
  onClose: () => void;
}

const noop = () => {};

interface IJobDetailsRefundDialogProps extends Omit<IJobDetailsCancelPopupProps, 'job'> {
  job: TJobDetailsCancelJob;
}

const JobDetailsRefundDialog = ({ job, onClose }: IJobDetailsRefundDialogProps) => {
  const { mutate: cancelJob, isPending } = useCancelJobMutation();

  // Can't be dismissed while pending, like CancelledJob: the redirect to the survey still lands,
  // and a reopened dialog would allow a second cancel.
  return (
    <CancelShiftRefundDialog
      job={job}
      isPending={isPending}
      onClose={isPending ? noop : onClose}
      onConfirm={() => cancelJob({ jobId: job.id, redirect: true })}
    />
  );
};

/**
 * Legacy JobDetails' cancel popup (a class component can't read the flag hook itself).
 * The v3 dialog is for the `StandardCancelFlow` `treatment` arm. Only peeks the flag:
 * JobDetails' Cancel button records the exposure.
 */
export const JobDetailsCancelPopup = ({ job, onClose }: IJobDetailsCancelPopupProps) => {
  const isStandardCancelFlow = useStandardCancelFlow();

  if (isStandardCancelFlow) {
    return job ? <JobDetailsRefundDialog job={job} onClose={onClose} /> : null;
  }

  return <CancelledJob closePopup={onClose} />;
};
